import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  calculatePurchase,
  calculateDonation,
  validateTransaction,
  PurchaseResponse,
  DonationResponse,
} from "../../services/pricingApiService";

// Utility function to save data to localStorage
const saveToLocalStorage = (key: string, data: unknown) => {
  localStorage.setItem(key, JSON.stringify(data));
};

// Define the initial state for the purchase slice
export interface PurchaseState {
  subtotal: number;
  tax: number;
  total: number;
  brokerFee: number;
  brokerFeeTax: number;
  brokerFeeTotal: number;
  totalCharge: number;
  token: string;
  createdAt: string;
  validUntil: string;
  country: string;
  validated: boolean;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

// Define the initial state for the donation slice
export interface DonationState {
  donationSubtotal: number;
  donationFeeRate: number;
  donationFee: number;
  donationFeeTax: number;
  donationFeeTotal: number;
  brokerFee: number;
  brokerFeeTax: number;
  brokerFeeTotal: number;
  totalCharge: number;
  token: string;
  createdAt: string;
  validUntil: string;
  validated: boolean;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

// Check local storage for initial states
export const createInitialPurchaseState = (): PurchaseState => ({
  subtotal: 0,
  tax: 0,
  total: 0,
  brokerFee: 0,
  brokerFeeTax: 0,
  brokerFeeTotal: 0,
  totalCharge: 0,
  token: "",
  createdAt: "",
  validUntil: "",
  country: "",
  validated: false,
  status: "idle",
  error: null,
});

export const createInitialDonationState = (): DonationState => ({
  donationSubtotal: 0,
  donationFeeRate: 0,
  donationFee: 0,
  donationFeeTax: 0,
  donationFeeTotal: 0,
  brokerFee: 0,
  brokerFeeTax: 0,
  brokerFeeTotal: 0,
  totalCharge: 0,
  token: "",
  createdAt: "",
  validUntil: "",
  validated: false,
  status: "idle",
  error: null,
});

// Async thunk for calculating a purchase
export const fetchPurchasePricing = createAsyncThunk(
  "pricing/fetchPurchasePricing",
  async ({ country }: { country: string }) => {
    const response: PurchaseResponse = await calculatePurchase(country);
    return response;
  }
);

// Async thunk for calculating a donation
export const fetchDonationPricing = createAsyncThunk(
  "pricing/fetchDonationPricing",
  async ({ price, country }: { price: number; country: string }) => {
    const response: DonationResponse = await calculateDonation(price, country);
    return response;
  }
);

// Async thunk for validating a transaction token
export const validateTransactionToken = createAsyncThunk(
  "pricing/validateTransactionToken",
  async ({
    token,
    paymentId,
    transactionType,
  }: {
    token: string;
    paymentId: string;
    transactionType: "purchase" | "donation";
  }) => {
    const response = await validateTransaction(
      token,
      paymentId,
      transactionType
    );
    return { ...response, transactionType };
  }
);

// Create the pricing slice with separate states for purchase and donation
const pricingSlice = createSlice({
  name: "pricing",
  initialState: {
    purchaseState: createInitialPurchaseState(),
    donationState: createInitialDonationState(),
  },
  reducers: {},
  extraReducers: (builder) => {
    // For fetchPurchasePricing
    builder.addCase(fetchPurchasePricing.pending, (state) => {
      state.purchaseState.status = "loading";
    });
    builder.addCase(fetchPurchasePricing.fulfilled, (state, action) => {
      const {
        subtotal,
        tax,
        total,
        broker_fee,
        broker_fee_tax,
        broker_fee_total,
        total_charge,
        token,
        created_at,
        valid_until,
        country,
        validated,
      } = action.payload;

      state.purchaseState = {
        subtotal,
        tax,
        total,
        brokerFee: broker_fee,
        brokerFeeTax: broker_fee_tax,
        brokerFeeTotal: broker_fee_total,
        totalCharge: total_charge,
        token,
        createdAt: created_at,
        validUntil: valid_until,
        country,
        validated,
        status: "succeeded",
        error: null,
      };

      // Save to local storage
      saveToLocalStorage("purchaseState", state.purchaseState);
    });
    builder.addCase(fetchPurchasePricing.rejected, (state, action) => {
      state.purchaseState.status = "failed";
      state.purchaseState.error =
        action.error.message || "Failed to fetch purchase pricing";
    });

    // For fetchDonationPricing
    builder.addCase(fetchDonationPricing.pending, (state) => {
      state.donationState.status = "loading";
    });
    builder.addCase(fetchDonationPricing.fulfilled, (state, action) => {
      const {
        donation_subtotal,
        donation_fee_rate,
        donation_fee,
        donation_fee_tax,
        donation_fee_total,
        broker_fee,
        broker_fee_tax,
        broker_fee_total,
        total_charge,
        token,
        created_at,
        valid_until,
        validated,
      } = action.payload;

      state.donationState = {
        donationSubtotal: donation_subtotal,
        donationFeeRate: donation_fee_rate,
        donationFee: donation_fee,
        donationFeeTax: donation_fee_tax,
        donationFeeTotal: donation_fee_total,
        brokerFee: broker_fee,
        brokerFeeTax: broker_fee_tax,
        brokerFeeTotal: broker_fee_total,
        totalCharge: total_charge,
        token,
        createdAt: created_at,
        validUntil: valid_until,
        validated,
        status: "succeeded",
        error: null,
      };

      // Save to local storage
      saveToLocalStorage("donationState", state.donationState);
    });
    builder.addCase(fetchDonationPricing.rejected, (state, action) => {
      state.donationState.status = "failed";
      state.donationState.error =
        action.error.message || "Failed to fetch donation pricing";
    });

    // For validateTransactionToken (conditional logic based on transaction type)
    builder.addCase(validateTransactionToken.pending, (state, action) => {
      const { transactionType } = action.meta.arg;
      if (transactionType === "purchase") {
        state.purchaseState.status = "loading";
      } else {
        state.donationState.status = "loading";
      }
    });
    builder.addCase(validateTransactionToken.fulfilled, (state, action) => {
      const { transactionType, validated } = action.payload;

      if (transactionType === "purchase") {
        state.purchaseState.status = "succeeded";
        state.purchaseState.validated = validated;
      } else {
        state.donationState.status = "succeeded";
        state.donationState.validated = validated;
      }
    });
    builder.addCase(validateTransactionToken.rejected, (state, action) => {
      const { transactionType } = action.meta.arg;

      if (transactionType === "purchase") {
        state.purchaseState.status = "failed";
        state.purchaseState.error =
          action.error.message || "Failed to validate purchase token";
      } else {
        state.donationState.status = "failed";
        state.donationState.error =
          action.error.message || "Failed to validate donation token";
      }
    });
  },
});

export default pricingSlice.reducer;
